import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import Image from 'next/image';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  User,
  UserRound,
  Box,
  Palette,
  Type,
  PawPrint,
  AppleIcon,
  Smile,
} from 'lucide-react';
import Layout from '@/layouts/_layout';
import AnchorLink from '@/components/ui/links/anchor-link';
import { useMe } from '@/data/user';
import { ModelType, TrainingSubjectType } from '@/types';
import { tutorials } from '@/components/models/tutorial';
import { useStartTrainingMutation } from '@/data/product';
import toast from 'react-hot-toast';
import FileInput from '@/components/ui/file-input';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { omitTypename } from '@/utils/omit-typename';
import Alert from '@/components/ui/alert';
import { getErrorMessage } from '@/data/utils/form-error';
import { useTranslation } from 'react-i18next';
import { useUploadMutationState } from '@/data/upload';
import { useModalAction } from '@/components/modal-views/context';
import { InsufficientCreditsModal } from '@/components/billing/insufficient-credits-modal';
import { GetServerSideProps } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { motion } from 'framer-motion';
import { trackEvent } from '@/data/utils/utils';
import { cn } from '@/lib/utils';
import { TrainingIcon } from '@/components/icons/training-icon';
import { LightningIcon } from '@/components/icons/lightning-icon';
import Pbucks from '@/assets/images/P-Bucks.svg';

const subjectTypeIcons: Record<TrainingSubjectType, React.ElementType> = {
  man: User,
  woman: UserRound,
  character: Smile,
  child: User,
  product: Box,
  style: Palette,
  pet: PawPrint,
  font: Type,
  food: AppleIcon,
  object: Box,
};

const getTutorialTypeFromSubjectType = (
  subjectType: TrainingSubjectType
): ModelType => {
  if (['man', 'woman', 'pet', 'child'].includes(subjectType)) {
    return 'Person';
  } else if (['product', 'object', 'food'].includes(subjectType)) {
    return 'Product';
  } else if (['font', 'style', 'character'].includes(subjectType)) {
    return 'Style';
  }
  return 'Person'; // default fallback
};

const productUpdateValidationSchema = yup.object().shape({
  name: yup.string().required('Name is required'),
  training_subject_type: yup
    .string()
    .nullable()
    .oneOf(Object.values(TrainingSubjectType), 'Invalid subject type')
    .required('Subject type is required'),
  training_samples: yup
    .mixed()
    .test('fileList', 'At least 4 images are required', (value) => {
      return value && value.length >= 1;
    })
    .required('Training samples are required'),
  // gallery: yup
  //   .array()
  //   // .min(4, 'At least 4 images are required')
  //   .required('Gallery is required'),
});

function getProductInputValues(values: ProductFormValues) {
  const { training_samples, name, training_subject_type } = values;
  return {
    name: name || 'Untitled Model',
    training_subject_type: training_subject_type,
    training_samples: training_samples?.map((gi: any) =>
      omitTypename(gi)
    ) as any[],
  };
}

interface ProductFormValues {
  name: string;
  training_subject_type: TrainingSubjectType;
  shop_id: string;
  training_samples: any[];
}

export function TrainModel() {
  const router = useRouter();
  const [modelType, setModelType] = useState<ModelType>('Person');
  const state = useUploadMutationState();
  const isUploading = state?.status === 'loading';
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { t } = useTranslation();
  const methods = useForm<ProductFormValues>({
    resolver: yupResolver(productUpdateValidationSchema),
    shouldUnregister: true,
    // defaultValues: {
    //   training_subject_type: 'man' as TrainingSubjectType, // Explicitly type as TrainingSubjectType
    // },
  });
  const {
    handleSubmit,
    control,
    setError,
    register,
    formState: { errors },
    watch,
  } = methods;
  const { me, hasSubscription } = useMe();
  const hasSufficientPoints =
    me?.wallet?.available_points &&
    me?.wallet?.available_points >
      Number(process.env.NEXT_PUBLIC_CREDITS_PER_MODEL);

  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const { openModal } = useModalAction();

  const { mutate: startTraining, isLoading: isCreatingModel } =
    useStartTrainingMutation({
      onSuccess: (data: any) => {
        router.push('/my-consistent-ai/overview');
      },
    });

  useEffect(() => {
    const { type } = router.query;
    if (
      type &&
      typeof type === 'string' &&
      Object.keys(tutorials).includes(type)
    ) {
      setModelType(type as ModelType);
    }
  }, [router.query]);

  // Add new useEffect to handle subject type changes
  useEffect(() => {
    const subjectType = methods.watch('training_subject_type');
    if (subjectType) {
      const tutorialType = getTutorialTypeFromSubjectType(subjectType);
      setModelType(tutorialType);
    }
  }, [methods.watch('training_subject_type')]);

  const onSubmit = (values: ProductFormValues) => {
    if (!me?.shops.length) {
      toast.error('Shop ID not found. Please contact support.');
      return;
    }

    // validate amount of training samples
    if (values.training_samples.length < 4) {
      toast.error('At least 4 images are required.');
      setError('training_samples', {
        type: 'manual',
        message: 'At least 4 images are required.',
      });
      return;
    }

    const availableCredits = me?.wallet?.available_points || 0;
    if (availableCredits < 1) {
      setShowCreditsModal(true);
      trackEvent('insufficient_credits_train', { credits: availableCredits });
      return;
    }

    const inputValues = {
      language: router.locale,
      ...getProductInputValues(values),
    };
    const shop_id = me?.shops[0].id;

    try {
      trackEvent('start_model_training', {
        ...inputValues,
        shop_id,
      });
      startTraining({
        ...inputValues,
        shop_id: shop_id,
      });
    } catch (error) {
      const serverErrors = getErrorMessage(error);
      Object.keys(serverErrors?.validation).forEach((field: any) => {
        setError(field.split('.')[1], {
          type: 'manual',
          message: serverErrors?.validation[field][0],
        });
      });
    }
  };

  return (
    <div className="flex w-full flex-col p-2 py-6 sm:p-4 sm:py-8 md:px-8 lg:px-12 lg:py-10 xl:px-40">
      <div className="mx-auto w-full space-y-6 px-0 sm:space-y-8 sm:px-2">
        {!hasSufficientPoints && (
          <div className="relative w-full rounded-lg bg-gradient-to-r from-brand/10 to-brand/5 p-3 dark:from-brand/20 dark:to-brand/10 sm:p-4">
            <div className="flex w-full flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h5 className="mb-1 text-base font-medium tracking-tight sm:text-lg">
                  Training models is only available on upgraded plans
                </h5>
                <div className="text-muted-foreground text-xs sm:text-sm">
                  Upgrade to a paid plan to train your own models. (starting at
                  $16/mo)
                </div>
              </div>
              <Button
                className="w-full max-w-sm bg-brand text-white hover:bg-brand/90 dark:bg-brand/90 dark:text-white dark:hover:bg-brand"
                onClick={() => {
                  setShowCreditsModal(true);
                  trackEvent('click_unlock_training', {
                    location: 'train_page',
                  });
                }}
              >
                <TrainingIcon className="mr-2 h-3 w-3 sm:h-4 sm:w-4" />
                Unlock Training
              </Button>
            </div>
          </div>
        )}

        <div className="flex flex-col gap-6 lg:flex-row lg:gap-6 xl:gap-8">
          <Card className="flex-1 border-1 border-brand bg-default-400/10 p-3 shadow-md backdrop-blur-sm hover:bg-default-200/20 dark:border-brand/50 dark:bg-default-400/20 dark:hover:bg-default-400/30 sm:p-4">
            <CardHeader className="p-0 sm:p-2">
              <CardTitle className="text-lg sm:text-xl">
                Train Your AI Model
              </CardTitle>
              <p className="text-muted-foreground text-xs sm:text-sm">
                Choose a name, type, and upload at least 4 photos to get
                started.
              </p>
            </CardHeader>
            <CardContent className="p-2 sm:p-4">
              {errorMessage && (
                <Alert
                  message={t(`common:${errorMessage}`)}
                  variant="error"
                  closeable={true}
                  className="mt-5"
                  onClose={() => setErrorMessage(null)}
                />
              )}
              <FormProvider {...methods}>
                <form
                  className="space-y-4 sm:space-y-6"
                  onSubmit={handleSubmit(onSubmit)}
                  noValidate
                >
                  <div className="space-y-1 sm:space-y-2">
                    <Label htmlFor="name">Name</Label>
                    <Input
                      id="name"
                      placeholder="e.g. Natalie Headshots"
                      className="bg-default-400/10 text-sm hover:bg-default-200/20 focus:bg-white dark:bg-default-400/20 dark:hover:bg-default-400/30 dark:focus:bg-default-100"
                      {...register('name')}
                    />
                    {errors.name?.message && (
                      <p className="text-xs text-red-500">
                        {t(errors.name?.message!)}
                      </p>
                    )}
                  </div>
                  <div className="space-y-1 sm:space-y-2">
                    <Label>Subject Type</Label>
                    <RadioGroup
                      {...register('training_subject_type')}
                      className="grid grid-cols-3 gap-1.5 sm:grid-cols-4 sm:gap-2"
                      onValueChange={(value) =>
                        methods.setValue(
                          'training_subject_type',
                          value as TrainingSubjectType
                        )
                      }
                    >
                      {Object.entries(subjectTypeIcons).map(([type, Icon]) => (
                        <div key={type}>
                          <RadioGroupItem
                            value={type}
                            id={type}
                            className="peer sr-only"
                          />
                          <Label
                            htmlFor={type}
                            className="border-muted flex cursor-pointer flex-col items-center justify-between rounded-md border-2 bg-default-400/10 p-2 hover:border-brand 
                              hover:bg-default-200/20 peer-data-[state=checked]:border-brand peer-data-[state=checked]:bg-brand/10 dark:bg-default-400/20 
                              dark:hover:bg-default-400/30 dark:peer-data-[state=checked]:bg-brand/30 sm:p-4"
                          >
                            <Icon className="mb-2 h-4 w-4 sm:mb-3 sm:h-6 sm:w-6" />
                            <span className="text-[10px] sm:text-xs">
                              {type.charAt(0).toUpperCase() + type.slice(1)}
                            </span>
                          </Label>
                        </div>
                      ))}
                    </RadioGroup>
                    {errors.training_subject_type?.message && (
                      <p className="text-xs text-red-500">
                        {t(errors.training_subject_type?.message!)}
                      </p>
                    )}
                  </div>
                  <div className="space-y-1 sm:space-y-2">
                    <Label>Samples</Label>
                    <div className="border-muted rounded-lg border-2 border-dashed bg-default-400/10 p-8 text-center hover:border-brand/50 hover:bg-default-200/20 dark:bg-default-400/20 dark:hover:bg-default-400/30">
                      <FileInput
                        name="training_samples"
                        control={control}
                        acceptFile={false}
                        multiple={true}
                      />
                    </div>
                    {errors.training_samples?.message && (
                      <p className="text-xs text-red-500">
                        {t(errors.training_samples?.message!)}
                      </p>
                    )}
                  </div>

                  <div className="rounded-lg bg-default-400/10 p-4 hover:bg-default-200/20 dark:bg-default-400/20 dark:hover:bg-default-400/30">
                    <h4 className="mb-2 font-medium">Tips</h4>
                    <ul className="text-muted-foreground space-y-1 text-sm">
                      <li>
                        • Use Square Samples: For the best results, upload
                        images with a 1:1 aspect ratio.
                      </li>
                      <li>
                        • Use Multiple Zoom Levels: Upload 10-20 high-quality
                        photos of varying distances.
                      </li>
                      <li>
                        • Add Variety: Change up poses, backgrounds, and angles
                        where the subject is looking.
                      </li>
                    </ul>
                  </div>
                  <Button
                    className="h-12 w-full bg-brand text-white hover:bg-brand/90 dark:bg-brand/90 dark:text-white dark:hover:bg-brand"
                    disabled={isCreatingModel || isUploading}
                    type="submit"
                  >
                    <div className="relative z-10 flex w-full items-center justify-between px-4">
                      <div className="flex items-center gap-3">
                        <LightningIcon className="h-3 w-3 sm:h-4 sm:w-4" />
                        <span className="font-medium">
                          {isUploading
                            ? 'Uploading images...'
                            : isCreatingModel
                            ? 'Creating...'
                            : hasSufficientPoints
                            ? 'Train Model'
                            : 'Unlock Training'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5">
                        <Image
                          src={Pbucks}
                          width={16}
                          height={16}
                          alt="P-Bucks"
                        />
                        <span>{process.env.NEXT_PUBLIC_CREDITS_PER_MODEL}</span>
                      </div>
                    </div>
                  </Button>
                  {!hasSufficientPoints && (
                    <p
                      className="cursor-pointer text-center text-sm text-red-400"
                      onClick={() => setShowCreditsModal(true)}
                    >
                      Upgrade to train your own models.
                    </p>
                  )}
                  <div className="rounded-lg bg-default-400/10 p-4 hover:bg-default-200/20 dark:bg-default-400/20 dark:hover:bg-default-400/30">
                    <h4 className="mb-2 font-medium">Important Details</h4>
                    <ul className="text-muted-foreground space-y-1 text-sm">
                      <li>• Training usually takes between 3 to 5 minutes.</li>
                      {/* <li>
                        • When your model is ready, we'll send you an email.
                      </li>
                      <li>• No nudes / NSFW images allowed.</li> */}
                    </ul>
                  </div>
                </form>
              </FormProvider>
            </CardContent>
          </Card>
          <Card className="flex-1 border-0 bg-default-400/10 p-3 shadow-md backdrop-blur-sm hover:bg-default-200/20 dark:bg-default-400/20 dark:hover:bg-default-400/30 sm:p-4">
            <CardHeader className="flex flex-col space-y-4 p-0 sm:p-2">
              <CardTitle className="text-lg sm:text-xl">Tutorial</CardTitle>
              <div className="relative flex w-full justify-center pb-4">
                <div className="flex w-full max-w-[300px] gap-1 rounded-full bg-default-400/10 p-0.5 backdrop-blur-lg dark:bg-default-400/20 sm:max-w-none">
                  {Object.keys(tutorials).map((type, index) => (
                    <motion.button
                      key={type}
                      onClick={() => setModelType(type as ModelType)}
                      className={cn(
                        'relative z-10 flex flex-1 items-center justify-center gap-1 rounded-full py-1.5 text-[10px] font-medium transition-colors sm:py-1.5 sm:text-xs',
                        'min-w-[60px] sm:min-w-[80px]',
                        modelType === type
                          ? 'text-white'
                          : 'text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
                      )}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {type}
                    </motion.button>
                  ))}
                  <motion.div
                    className="absolute left-0.5 top-0.5 h-[26px] rounded-full bg-brand"
                    style={{
                      width: `${100 / Object.keys(tutorials).length}%`,
                    }}
                    initial={false}
                    animate={{
                      x: `${Object.keys(tutorials).indexOf(modelType) * 100}%`,
                    }}
                    transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className="h-full">
              <ScrollArea className="h-full">
                {Object.entries(tutorials).map(([type, steps]) => (
                  <motion.div
                    key={type}
                    className={`${type !== modelType ? 'hidden' : ''}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="space-y-8 pb-8 pr-4">
                      <div>
                        <h3 className="mb-4 text-lg font-semibold">
                          How to get results:
                        </h3>
                        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                          {steps[0].images.slice(0, 4).map((src, i) => (
                            <div
                              key={i}
                              className="bg-muted relative aspect-square overflow-hidden rounded-md"
                            >
                              <Image
                                src={src}
                                alt={`Example ${i + 1}`}
                                width={100}
                                height={100}
                                className="absolute top-0 left-0 h-full w-full object-cover object-top"
                              />
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="space-y-6 pb-8">
                        <div>
                          <h4 className="mb-2 flex items-center gap-2 font-medium">
                            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-brand text-xs text-white">
                              ✓
                            </span>
                            {steps[0].title}
                          </h4>
                          <p className="text-muted-foreground text-sm">
                            {steps[0].description}
                          </p>
                        </div>

                        <div>
                          <h4 className="mb-2 flex items-center gap-2 font-medium">
                            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-brand text-xs text-white">
                              ✓
                            </span>
                            {steps[1].title}
                          </h4>
                          <p className="text-muted-foreground mb-4 text-sm">
                            {steps[1].description}
                          </p>
                          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                            {steps[1].images.slice(0, 4).map((src, i) => (
                              <div
                                key={i}
                                className="bg-muted relative aspect-square overflow-hidden rounded-md"
                              >
                                <Image
                                  src={src}
                                  alt={`Good example ${i + 1}`}
                                  width={100}
                                  height={100}
                                  className="absolute top-0 bg-white left-0 h-full w-full object-cover object-top"
                                />
                              </div>
                            ))}
                          </div>
                        </div>

                        <div>
                          <h4 className="mb-2 flex items-center gap-2 font-medium text-red-500">
                            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs text-white">
                              ✗
                            </span>
                            {steps[2].title}
                          </h4>
                          <p className="text-muted-foreground mb-4 text-sm">
                            {steps[2].description}
                          </p>
                          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                            {steps[2].images.slice(0, 3).map((src, i) => (
                              <div
                                key={i}
                                className="bg-muted aspect-square overflow-hidden rounded-md"
                              >
                                <Image
                                  src={src}
                                  alt={`Bad example ${i + 1}`}
                                  width={100}
                                  height={100}
                                  className="h-full w-full object-cover"
                                />
                              </div>
                            ))}
                          </div>
                        </div>

                        <div>
                          <h4 className="mb-2 flex items-center gap-2 font-medium">
                            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-brand text-xs text-white">
                              ✓
                            </span>
                            Train your model
                          </h4>
                          <p className="text-muted-foreground text-sm">
                            Training your model takes ~5 minutes. You can leave
                            the page and come back later.
                          </p>
                        </div>

                        <div>
                          <h4 className="mb-2 flex items-center gap-2 font-medium">
                            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-brand text-xs text-white">
                              ✓
                            </span>
                            Generate Images
                          </h4>
                          <p className="text-muted-foreground text-sm">
                            Once your model is trained, you can generate images
                            using prompts. Make sure to include the subject
                            keyword in your prompts.
                          </p>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>

      <InsufficientCreditsModal
        isOpen={showCreditsModal}
        onClose={() => setShowCreditsModal(false)}
        variant={hasSubscription ? 'credits' : 'subscription'}
      />
    </div>
  );
}

TrainModel.authorization = true;
TrainModel.getLayout = function getLayout(page: React.ReactElement) {
  return <Layout>{page}</Layout>;
};

export default TrainModel;

// Server-side props fetching
export const getServerSideProps: GetServerSideProps = async ({ locale }) => {
  return {
    props: {
      ...(await serverSideTranslations(locale!, ['common'])),
    },
  };
};
